import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { message, Modal } from "antd";
import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import * as services from "../services/branchApi";
import { setCurrentBranch, setIsLoading } from "../slices/companySlice";
import { useTranslation } from "react-i18next";

function toInitialValues(initialValues = {}) {
  const {
    province = {},
    district = {},
    subdistrict = {},
    ...rest
  } = initialValues;
  const modifiedValues = {
    ...rest,
    branchAddressStatus: initialValues.address != null,
    branchCompensateStatus: initialValues.bankAccountNo != null,
    statusFlag: initialValues.statusFlag === "ACTIVE" ? "ACTIVE" : "INACTIVE",
    provinceId: province?.provinceId,
    districtId: district?.districtId,
    subDistrictId: subdistrict?.subDistrictId,
  };
  return modifiedValues;
}

function toParams(values) {
  const modifiedValues = {
    ...values,
    branchId: values.id,
    phoneNumber: "0",
    branchAddressStatus: values.branchAddressStatus ? "Y" : "N",
    branchCompensationStatus: values.branchCompensateStatus ? "Y" : "N",
  };
  return modifiedValues;
}

function useCompanyBranch(props) {
  const { createMode = false, onEdit = () => { }, onCancel = () => { } } = props;
  const { t } = useTranslation();

  const {
    sourceProvinceOptions = [],
    sourceDistrictOptions = [],
    sourceSubDistrictOptions = [],
    getProvinceOptions,
    getDistrictOptions,
    getSubDistrictOptions,
    clearDistrictOptions,
    clearSubDistrictOptions,
  } = useOptions();

  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentBranch = useSelector(
    (state) => state.company.currentBranch || {}
  );

  const initialValues = useMemo(() => {
    return toInitialValues(currentBranch);
  }, [currentBranch]);

  const provinceOptionsTh = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt?.provinceName,
      value: opt?.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  const provinceOptionsEn = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt.provinceNameEn,
      value: opt?.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  const districtOptionsTh = useMemo(() => {
    const modifiedOptions = sourceDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.districtName,
      value: opt.districtId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceDistrictOptions]);

  const districtOptionsEn = useMemo(() => {
    const modifiedOptions = sourceDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.districtNameEn,
      value: opt.districtId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceDistrictOptions]);

  const subDistrictOptionsTh = useMemo(() => {
    const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
      ...opt,
      label: opt?.subDistrictName,
      value: opt?.subDistrictId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceSubDistrictOptions]);

  const subDistrictOptionsEn = useMemo(() => {
    const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
      ...opt,
      label: opt?.subDistrictNameEn,
      value: opt?.subDistrictId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceSubDistrictOptions]);

  const getBranch = useCallback(
    async (branchId) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          branchId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.getBranch(values);
        dispatch(setCurrentBranch(response.data));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createBranch = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.createBranch(toParams(values));
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  const editBranch = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.editBranch(toParams(values));
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = {
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (createMode ? t("dialog.confirmation.create").replace("{entity}", t("branch.header")) :
          t("dialog.confirmation.edit").replace("{entity}", t("branch.header"))
        ),
        onOk() {
          if (currentBranch.id && currentBranch.id !== " ") {
            editBranch(modifiedValues);
          } else {
            createBranch(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, currentBranch, createBranch, editBranch]
  );

  const handleProviceChange = useCallback(
    (val) => {
      clearDistrictOptions();
      clearSubDistrictOptions();
      if (val) {
        getDistrictOptions({ provinceId: val });
      }
    },
    [clearDistrictOptions, clearSubDistrictOptions, getDistrictOptions]
  );

  const handleDistrictChange = useCallback(
    (val) => {
      clearSubDistrictOptions();
      if (val) {
        getSubDistrictOptions({ districtId: val });
      }
    },
    [clearSubDistrictOptions, getSubDistrictOptions]
  );

  useEffect(() => {
    getProvinceOptions();
  }, [getProvinceOptions]);

  useEffect(() => {
    if (currentBranch.id) {
      getBranch(currentBranch.id);
    }
  }, [currentBranch.id, getBranch]);

  useEffect(() => {
    if (currentBranch && !createMode) {
      const { province = {}, district = {} } = currentBranch;
      if (province?.provinceId)
        getDistrictOptions({ provinceId: province?.provinceCode });
      if (district?.districtId)
        getSubDistrictOptions({ districtId: district?.districtCodeId });
    }
  }, [createMode, currentBranch, getDistrictOptions, getSubDistrictOptions]);

  return {
    isLoading,
    createMode,
    initialValues,
    currentBranch,
    provinceOptionsTh,
    provinceOptionsEn,
    districtOptionsTh,
    districtOptionsEn,
    subDistrictOptionsTh,
    subDistrictOptionsEn,
    onEdit,
    onCancel,
    onSubmit,
    handleProviceChange,
    handleDistrictChange,
  };
}

export default useCompanyBranch;
